import { ReactComponent as TagIconSVG } from 'assets/images/location/local-offer.svg';
import { ReactComponent as UserIconSVG } from 'assets/images/location/person-pin.svg';
import { ReactComponent as ServicesIconSVG } from 'assets/images/location/restaurant-menu.svg';
import classnames from 'classnames';
import AddDiscoveryConfirm from 'components/AddDiscoveryConfirm';
import AddDiscoveryPlaceImages from 'components/AddDiscoveryPlaceImages';
import { placeImagesRef } from 'components/AddDiscoveryPlaceImages/AddDiscoveryPlaceImages';
import AddDiscoveryPlaceInfo, { placeInfoRef } from 'components/AddDiscoveryPlaceInfo';
import AddDiscoverySuccess from 'components/AddDiscoverySuccess';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Container, Spinner, TabContent, TabPane } from 'reactstrap';
import useDiscoveryCreateStore, { initialFormData } from 'store/zustand/useDiscoveryCreateStore';
import { useParams, useHistory } from 'react-router-dom';
import ENDPOINT from 'constants/endPoint';
import styles from './ManagerDiscoveryPlaceEdit.module.scss';

const TITLE = 'Chỉnh sửa địa điểm khám phá';

const TAB = {
  INFO: 'INFO',
  IMAGES: 'IMAGES',
  CONFIRM: 'CONFIRM',
  SUCCESS: 'SUCCESS',
};

const breadcrumbList = [
  {
    name: 'Trang chủ',
    isActive: false,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
  {
    name: TITLE,
    isActive: true,
    link: '',
  },
];

const NAV_TAB = [
  {
    icon: UserIconSVG,
    title: 'Thông tin cơ bản',
    value: TAB.INFO,
  },
  {
    icon: TagIconSVG,
    title: 'Hình ảnh',
    value: TAB.IMAGES,
  },
  {
    icon: ServicesIconSVG,
    title: 'Xác nhận',
    value: TAB.CONFIRM,
  },
];

function ManagerDiscoveryPlaceEdit(props) {
  const history = useHistory();
  const { discoverPlaceDetails: item } = props;

  const { formData, setFormData } = useDiscoveryCreateStore();
  const { isInfoValid, isImagesValid } = formData;
  const [step, setStep] = React.useState(TAB.INFO);

  const [isReady, setIsReady] = React.useState(false);

  const stepAvailable = React.useMemo(
    () => [
      step,
      ...(isInfoValid ? [TAB.INFO] : []),
      ...(isImagesValid ? [TAB.IMAGES] : []),
      ...(isInfoValid && isImagesValid ? [TAB.CONFIRM] : []),
    ],
    [step, isInfoValid, isImagesValid],
  );

  useEffect(() => () => {
    // Clear form data
    setFormData(initialFormData);
  }, []);

  useEffect(() => {
    if (!item) return;

    setTimeout(() => {
      setIsReady(true);
    }, 100);

    setFormData({
      province: item?.provinceCode || '',
      district: item?.districtCode || '',
      ward: item?.wardCode || '',
      address: item?.address || '',
      coordinates:
        item?.location?.coordinates && Array.isArray(item.location.coordinates)
          ? item
            .location
            .coordinates
            .map((i) => i?.toString().trim())
            .join(',')
          : '',
      name: item?.name || '',
      serviceHoursFrom: item?.hoursOfService?.from || '',
      serviceHoursTo: item?.hoursOfService?.to || '',
      priceRangeFrom: item?.priceRange?.from ?? '',
      description: item?.description || '',
      contactName: item?.contactName || '',
      contactEmail: item?.contactEmail || '',
      contactPhone: item?.phoneNumber || '',

      nameEN: item?.nameEN || '',
      discoverCategories: Array.isArray(item?.discoverCategories)
        ? item.discoverCategories.map((i) => ({
          ...i,
          label: i?.name || '',
          value: i?._id || '',
        }))
        : [],
      descriptionEN: item?.descriptionEN || '',
      slug: item?.slug || '',
      discoverProvince: item?.discoverProvince?._id || item?.discoverProvince || '',

      images: [item?.previewImage, ...(Array.isArray(item?.images) ? item.images : [])]
        .filter(Boolean)
        .map((i, index) => ({
          image: i,
          id: new Date().getTime() + index,
          checked: true,
        })),
    });
  }, [item, setFormData]);

  if (!isReady) {
    return <Spinner />;
  }

  return (
    <>
      <div className="page-content">
        <Meta name={TITLE} />

        <Container fluid>
          <Breadcrumb pageTitle={TITLE} breadcrumbList={breadcrumbList} />

          {step === TAB.SUCCESS && (
            <AddDiscoverySuccess
              placeName={formData?.name || item?.name || ''}
              slug={formData?.slug || item?.slug || item?._id || ''}
              onManage={() => {
                history.push(ENDPOINT.MANAGER_DISCOVERY_PLACE);
              }}
            />
          )}

          {step !== TAB.SUCCESS && (
            <div className={styles.Page}>
              <ul className={classnames(styles.NavMenu, 'custom-scrollbar')}>
                {NAV_TAB.map(({ icon: Icon, title, value }) => (
                  <li key={value}>
                    <Button
                      isDefault
                      classes={classnames(
                        styles.NavMenuItem,
                        stepAvailable.includes(value) &&
                        step !== value &&
                        styles.NavMenuItemDone,
                        step === value && styles.NavMenuItemActive,
                      )}
                      onClick={() => {
                        if (step === TAB.INFO) {
                          placeInfoRef.current.saveData();
                        }

                        if (step === TAB.IMAGES) {
                          placeImagesRef.current.saveData();
                        }

                        if (value === TAB.IMAGES && !isInfoValid) { return; }
                        if (value === TAB.CONFIRM && (!isInfoValid || !isImagesValid)) return;

                        setStep(value);
                      }}
                    >
                      <Icon />

                      <p>{title}</p>
                    </Button>
                  </li>
                ))}
              </ul>

              <section className={styles.NavBody}>
                <TabContent activeTab={step}>
                  <TabPane tabId={TAB.INFO}>
                    <AddDiscoveryPlaceInfo
                      onNext={() => setStep(TAB.IMAGES)}
                      step={step}
                    />
                  </TabPane>

                  <TabPane tabId={TAB.IMAGES}>
                    <AddDiscoveryPlaceImages
                      onBack={() => setStep(TAB.INFO)}
                      onNext={() => setStep(TAB.CONFIRM)}
                      step={step}
                    />
                  </TabPane>

                  <TabPane tabId={TAB.CONFIRM}>
                    <AddDiscoveryConfirm
                      onBack={() => setStep(TAB.IMAGES)}
                      onNext={() => setStep(TAB.SUCCESS)}
                      step={step}
                    />
                  </TabPane>
                </TabContent>
              </section>
            </div>
          )}
        </Container>
      </div>
    </>
  );
}

function DetailsWrapper(props) {
  const { id } = useParams();

  const {
    discoverPlaceDetails,
    discoverPlaceDetailsLoading,
    actionGetDiscoverPlaceDetails,
  } = props;

  useEffect(() => {
    actionGetDiscoverPlaceDetails(id);
  }, [actionGetDiscoverPlaceDetails, id]);

  if (discoverPlaceDetailsLoading) {
    return <Spinner />;
  }

  if (!discoverPlaceDetails._id && !discoverPlaceDetailsLoading) {
    return <div>Không tìm thấy điểm khám phá</div>;
  }

  return (
    <ManagerDiscoveryPlaceEdit
      discoverPlaceDetails={discoverPlaceDetails}
    />
  );
}

ManagerDiscoveryPlaceEdit.propTypes = {
  discoverPlaceDetails: PropTypes.instanceOf(Object).isRequired,
};

DetailsWrapper.propTypes = {
  discoverPlaceDetails: PropTypes.instanceOf(Object).isRequired,
  discoverPlaceDetailsLoading: PropTypes.bool.isRequired,
  actionGetDiscoverPlaceDetails: PropTypes.func.isRequired,
};

export default DetailsWrapper;
